// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import { type UserId, useEvolveGet, useEvolvePatch, useEvolvePost } from '@evolvemep/api-ts';

import { ssoRedirectUri } from '../Login/helper';

const invitationTypes = ['project', 'department', 'company'] as const;
export type InvitationType = (typeof invitationTypes)[number];
export const isInvitationType = (type: string | undefined | null): type is InvitationType =>
  invitationTypes.includes(type as InvitationType);

const resourceUrlMap: Record<InvitationType, string> = {
  company: 'user',
  department: 'departmentUser',
  project: 'projectUser',
};

export const useConfirmUser = (showError = false) => {
  const { apiCall, loading } = useEvolvePatch('admin/:resource/:id/confirmation', { alertOnError: showError });
  const confirmUser = useCallback(
    (
      id: string,
      type: InvitationType,
      body: {
        confirmationCode: string;
        password?: string;
      },
    ) => apiCall(body, { url: `admin/${resourceUrlMap[type]}/${id}/confirmation` }),
    [apiCall],
  );
  return useMemo(() => ({ confirmUser, confirming: loading }), [confirmUser, loading]);
};

export const useConfirmUserSso = (throwError = false) => {
  const { apiCall: confirmUserSso, loading: confirmingSso } = useEvolvePost(
    'admin/authentication/confirmInvitationSso',
    { alertOnError: !throwError },
  );
  return useMemo(() => ({ confirmUserSso, confirmingSso }), [confirmUserSso, confirmingSso]);
};

export const useGetAuthenticationUrl = () => {
  const { apiCall, loading } = useEvolveGet(`admin/authentication/url/:emailOrUserId`);

  const getAuthenticationUrl = useCallback(
    (emailOrUserId: `email/${string}` | `user/${UserId}`) =>
      apiCall({
        url: `admin/authentication/url/${emailOrUserId}`,
        params: {
          redirectUrl: ssoRedirectUri,
        },
      }),
    [apiCall],
  );
  return useMemo(() => ({ getAuthenticationUrl, loading }), [getAuthenticationUrl, loading]);
};

export const useGetTokenSso = () => {
  const { apiCall, loading } = useEvolveGet(`admin/authentication/sso/token/:code`);
  const getTokenSso = useCallback(
    (code: string) => apiCall({ url: `admin/authentication/sso/token/${code}` }),
    [apiCall],
  );
  return useMemo(() => ({ getTokenSso, confirming: loading }), [getTokenSso, loading]);
};
